import { useEffect, type RefObject } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

import { fg } from '@confluence/feature-gating';

import { getLoadedBylineMetrics, loadBylineMetrics } from './loadBylineMetrics.ts';
import { BYLINE_LAYOUT_WINDOW_MS } from './bylineMetricsConstants.ts';

export const useBylineLayoutMetrics = (
	contentId: string,
	bylineRef: RefObject<HTMLDivElement>,
): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (!fg('confluence_byline_cls_telemetry') || !bylineRef.current) return;
		const root = bylineRef.current;
		const interaction = getActiveInteraction();

		if (!interaction || interaction.type !== 'page_load' || document.visibilityState === 'hidden')
			return;
		let cancelled = false;
		let cleanup: (() => void) | undefined;
		let pendingTimer: ReturnType<typeof setTimeout> | undefined;
		const cancel = () => {
			cancelled = true;
			removePendingListeners();
		};
		const onHidden = () => {
			if (document.visibilityState === 'hidden') cancel();
		};
		const removePendingListeners = () => {
			clearTimeout(pendingTimer);
			document.removeEventListener('visibilitychange', onHidden);
			window.removeEventListener('pagehide', cancel);
			window.removeEventListener('pointerdown', cancel, true);
			window.removeEventListener('keydown', cancel, true);
			window.removeEventListener('resize', cancel);
		};
		const start = (metrics: ReturnType<typeof getLoadedBylineMetrics>) => {
			removePendingListeners();
			if (
				cancelled ||
				!metrics ||
				!fg('confluence_byline_cls_telemetry') ||
				getActiveInteraction() !== interaction
			)
				return;
			cleanup = metrics.observeBylineLayout(
				root,
				(attributes) => {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'measured',
							actionSubject: 'bylineApps',
							source: 'bylineLayout',
							attributes,
						},
					}).fire();
				},
				contentId,
			);
		};
		const loaded = getLoadedBylineMetrics();
		if (loaded) start(loaded);
		else {
			// Do not begin a supposedly uninterrupted window after input/hiding/resizing during download.
			document.addEventListener('visibilitychange', onHidden);
			window.addEventListener('pagehide', cancel);
			window.addEventListener('pointerdown', cancel, true);
			window.addEventListener('keydown', cancel, true);
			window.addEventListener('resize', cancel);
			pendingTimer = setTimeout(
				cancel,
				Math.max(0, interaction.start + BYLINE_LAYOUT_WINDOW_MS - performance.now()),
			);
			void loadBylineMetrics().then(start);
		}
		return () => {
			cancelled = true;
			removePendingListeners();
			cleanup?.();
		};
	}, [contentId, bylineRef, createAnalyticsEvent]);
};
